import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from '@material-ui/core'
import { useState } from 'react'

import Button from '@/components/button'
import { UseGetInfestationFromDiagnosisResponse } from '@/hooks/queries/use-get-infestation-from-diagnosis'
import { useCreatePulverizationMap } from '@/pages/diagnosis-view/contexts/create-pulverization-map-context'
import { transformUnderscoreInSpaceAndUppercaseFirstLetter } from '@/utils/format-strings'

import { useStyles } from './styles'

type LayersMap = UseGetInfestationFromDiagnosisResponse['layersMap'][number]

function transformInLayersToMerge(layers: LayersMap[]) {
  return layers.reduce(
    (acc, layer) => ({ ...acc, [layer.name]: false }),
    {} as Record<string, boolean>,
  )
}

export function MergeLayers() {
  const classes = useStyles()

  const {
    infestationData,
    nextStep,
    previousStep,
    addToMergeLayers,
    removeFromMergeLayers,
  } = useCreatePulverizationMap()

  const { layersMap, mergedLayersMap } = infestationData!

  const [layersToMerge, setLayersToMerge] = useState(() =>
    transformInLayersToMerge(layersMap),
  )

  function handleToggleMergeLayer(layerName: string) {
    setLayersToMerge((state) => ({ ...state, [layerName]: !state[layerName] }))
  }

  function handleMergeLayers() {
    const layersList = Object.entries(layersToMerge)
      .filter(([, isMerged]) => isMerged)
      .map(([layerName]) => layerName)

    addToMergeLayers(layersList)
    setLayersToMerge(transformInLayersToMerge(layersMap))
  }

  return (
    <>
      <div className={classes.availableLayersContainer}>
        <header>
          <Typography variant='h3'>
            Mescle as camadas para pulverização
          </Typography>
        </header>

        <FormGroup>
          {layersMap.map(
            (layer) =>
              !layer.removed && (
                <FormControlLabel
                  key={layer.name}
                  name={layer.name}
                  control={<Checkbox size='small' />}
                  disabled={!!layer.mergedName}
                  checked={layersToMerge[layer.name]}
                  onChange={() => handleToggleMergeLayer(layer.name)}
                  label={
                    <>
                      <span>
                        {transformUnderscoreInSpaceAndUppercaseFirstLetter(
                          layer.name,
                        )}
                      </span>
                      <Box
                        bgcolor={layer.colorPalette}
                        minWidth={16}
                        minHeight={16}
                        maxWidth={16}
                        maxHeight={16}
                        borderRadius={4}
                        marginX='auto'
                        style={{
                          filter: layer.mergedName ? 'opacity(0.2)' : 'none',
                          border: layer.mergedName ? '1px solid black' : 'none',
                        }}
                      />
                    </>
                  }
                />
              ),
          )}
        </FormGroup>

        <Button
          outlined
          onClick={handleMergeLayers}
          disabled={Object.values(layersToMerge).filter(Boolean).length < 2}
        >
          Adicionar mesclagem
        </Button>
      </div>
      <div className={classes.selectedLayersContainer}>
        <header>
          <Typography variant='h3'>Camadas mescladas</Typography>
        </header>

        <ul>
          {mergedLayersMap.map((layer, index) => (
            <li key={layer.name}>
              <span>
                {transformUnderscoreInSpaceAndUppercaseFirstLetter(layer.name)}
                <Box
                  bgcolor={layer.colorPalette}
                  minWidth={16}
                  minHeight={16}
                  maxWidth={16}
                  maxHeight={16}
                  borderRadius={4}
                  display='inline-block'
                />
              </span>
              <IconButton
                size='medium'
                color='secondary'
                aria-label={`Remover ${layer.name}`}
                onClick={() => removeFromMergeLayers(index)}
              >
                <span className='material-symbols-outlined'>delete</span>
              </IconButton>
            </li>
          ))}
        </ul>
      </div>

      <div className={classes.buttonContainer}>
        <Button outlined onClick={previousStep}>
          Voltar
        </Button>
        <Button
          onClick={nextStep}
          disabled={layersMap.every((layer) => layer.removed)}
        >
          Avançar
        </Button>
      </div>
    </>
  )
}
